import React, { useState, useRef, useEffect } from "react";
import QrScanner from "../Qr/QrScanner";
import { assignLocationToAsset, assignParentAsset, fetchAssetDetailsById, updateAsset } from "../../services/assetService";
import { fetchLocationDetailsById, assignLocationToparent, updateLocation } from "../../services/locationService";
import AlertModal from "../Modal/AlertModal";
import { FaExclamationTriangle } from "react-icons/fa";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import QRTokenService from "../../services/QRTokenService";
import ActiveScanService from "../../services/ActiveScanService";
import styles from "./Home.module.css";

const Home = () => {
  const barcodeInputRef = useRef(null);
  const [qrScanning, setQrScanning] = useState(false);
  const streamRef = useRef(null); // Stream ref
  const [loading, setLoading] = useState(false);
  const [recentScans, setRecentScans] = useState([]);
  const navigate = useNavigate();
  const isInitialLoading = useRef(true);

  const [firstItemId, setFirstItemId] = useState(-1);
  const [secondItemId, setSecondItemId] = useState(-1);
  const [firstItemType, setFirstItemType] = useState('');
  const [secondItemType, setSecondItemType] = useState('');
  const [firstItemDetails, setFirstItemDetails] = useState({});
  const [secondItemDetails, setSecondItemDetails] = useState({});

  const [firstCurrentlyMappedTo, setFirstCurrentlyMappedTo] = useState([]);
  const [secondCurrentlyMappedTo, setSecondCurrentlyMappedTo] = useState([]);

  useEffect(() => {
    fetchRecentScans();
    fetchActiveScan();
    // Arriving via an "Open in Scanner" link: launch the camera right away
    if (new URLSearchParams(window.location.search).get("page") === "scanner") {
      setQrScanning(true);
    }
  }, []);

  const fetchActiveScan = async () => {
    isInitialLoading.current = true;
    try {
      const activeScan = await ActiveScanService.getActiveScan();
      if (activeScan) {
        if (activeScan.firstItemId !== undefined && activeScan.firstItemId !== null && activeScan.firstItemId !== -1) {
          await handleAssetOrLocationDetails(activeScan.firstItemId, activeScan.firstItemType, 1);
        }
        if (activeScan.secondItemId !== undefined && activeScan.secondItemId !== null && activeScan.secondItemId !== -1) {
          await handleAssetOrLocationDetails(activeScan.secondItemId, activeScan.secondItemType, 2);
        }
      }
    } catch (error) {
      console.error("Failed to fetch active scan:", error);
    } finally {
      isInitialLoading.current = false;
    }
  };

  // Generic helper to fetch details and set state for first or second item
  const handleAssetOrLocationDetails = async (id, type, position) => {
    const setItemIdFunction = position === 1 ? setFirstItemId : setSecondItemId;
    const setItemTypeFunction = position === 1 ? setFirstItemType : setSecondItemType;
    const setItemFunction = position === 1 ? setFirstItemDetails : setSecondItemDetails;
    const setCurrMapFunction = position === 1 ? setFirstCurrentlyMappedTo : setSecondCurrentlyMappedTo;

    if (type === "asset") {
      handleAssetDetails(id, type, setItemFunction, setCurrMapFunction, setItemIdFunction, setItemTypeFunction);
    } else if (type === "location") {
      handleLocationDetails(id, type, setItemFunction, setCurrMapFunction, setItemIdFunction, setItemTypeFunction);
    }
  };

  // Persist active scan whenever items change
  useEffect(() => {
    const saveState = async () => {
      // Don't save during initial load or if both are empty/initial
      if (isInitialLoading.current || (firstItemId === -1 && secondItemId === -1)) return;
      
      await ActiveScanService.saveActiveScan({
        firstItemId,
        firstItemType,
        secondItemId,
        secondItemType
      });
    };
    saveState();
  }, [firstItemId, firstItemType, secondItemId, secondItemType]);

  const fetchRecentScans = async () => {
    try {
      const data = await QRTokenService.getAllTokens();
      // Filter for unassigned tokens with a scan timestamp, sorted by newest scan
      const pending = data
        .filter(t => !t.assigned && t.lastScannedAt)
        .sort((a, b) => new Date(b.lastScannedAt) - new Date(a.lastScannedAt));
      setRecentScans(pending);
    } catch (error) {
      console.error("Failed to fetch recent scans:", error);
    }
  };

  const handleDismissScan = async (e, token) => {
    e.stopPropagation();
    try {
      await QRTokenService.clearScanStatus(token);
      setRecentScans(prev => prev.filter(s => s.token !== token));
    } catch (error) {
      toast.error("Failed to dismiss scan");
    }
  };


  const [showAlert, setShowAlert] = useState(false);
  const [showAlertMessage, setShowAlertMessage] = useState("");

  const handleCloseAlert = () => {
    setShowAlert(false);
  };

  const assignLocation = async (assetId, locationId) => {
    try {
      setLoading(true);
      const res = await assignLocationToAsset(assetId, locationId);
      if (res.status === 200) {
        toast.success("Container assigned successfully!");
        await clearScans();
      } else {
        toast.error("Failed to assign container. Status: " + res.status);
      }
    } catch (error) {
      console.error("Error assigning container:", error);
      toast.error("Failed to assign container.");
    } finally {
      setLoading(false);
    }
  };

  const assignChildLocation = async (locationChildId, locationParentId) => {
    try {
      setLoading(true);
      const res = await assignLocationToparent(locationChildId, locationParentId);
      if (res.status === 200) {
        toast.success("Child container assigned successfully!");
        await clearScans();
      } else {
        toast.error("Failed to assign child container. Status: " + res.status);
      }
    } catch (error) {
      console.error("Error assigning child container:", error);
      toast.error(`Failed to assign child container. ${error.response.data}`);
    } finally {
      setLoading(false);
    }
  };

  const assignParent = async (assetId, parentId) => {
    try {
      setLoading(true);
      const res = await assignParentAsset(assetId, parentId);
      if (res.status === 200) {
        toast.success("Asset assigned to parent successfully!");
        await clearScans();
      } else {
        toast.error("Failed to assign parent asset. Status: " + res.status);
      }
    } catch (error) {
      console.error("Error assigning parent asset:", error);
      toast.error("Failed to assign parent asset.");
    } finally {
      setLoading(false);
    }
  };

  const getAssetDetails = async (assetId) => {
    if (!Number.isInteger(Number(assetId)) || Number(assetId) <= 0) {
      toast.error(`Invalid Asset ID: ${assetId}`);
      return null;
    }
    try {
      const res = await fetchAssetDetailsById(assetId);
      if (res.status === 200) {
        return res.data;
      }
      return null;
    } catch (error) {
      console.error("Error fetching asset details:", error);
      return null;
    }
  };

  const buildHierarchy = (item, type) => {
    if (!item) return "";
    const name = item.name || "Unknown";
    const parent = type === 'asset' ? item.parentAsset : item.parentLocation;
    if (parent) {
      return `${buildHierarchy(parent, type)} > ${name}`;
    }
    return name;
  };

  const handleAssetDetails = async (
    assetId,
    itemType,
    setItemFunction,
    setCurrMapFunction,
    setItemIdFunction,
    setItemTypeFunction
  ) => {
    const assetData = await getAssetDetails(assetId);
    if (assetData) {
      if (firstItemId !== -1) {
        if (firstItemType === 'asset' && Number(firstItemId) === Number(assetId)) {
          const msg = "Can't map an asset to itself.";
          setShowAlert(true);
          setShowAlertMessage(msg);
          return;
        }
      }

      setItemIdFunction(assetId);
      setItemTypeFunction(itemType);

      const isMappedToLocation = assetData?.location !== null;
      const isMappedToParent = assetData?.parentAsset !== null;
      const mappings = [];
      if (isMappedToLocation) {
        mappings.push({ name: buildHierarchy(assetData.location, 'location'), type: 'container' });
      }
      if (isMappedToParent) {
        mappings.push({ name: buildHierarchy(assetData.parentAsset, 'asset'), type: 'parent asset' });
      }

      setCurrMapFunction(mappings);
      setItemFunction(assetData);
    }
  };

  const getLocationDetails = async (locationId) => {
    if (!Number.isInteger(Number(locationId)) || Number(locationId) <= 0) {
      toast.error(`Invalid Container ID: ${locationId}`);
      return null;
    }
    try {
      const res = await fetchLocationDetailsById(locationId);
      if (res.status === 200) {
        return res.data;
      }
      return null;
    } catch (error) {
      console.error("Error fetching container details:", error);
      return null;
    }
  };

  const handleLocationDetails = async (
    locationId,
    itemType,
    setItemFunction,
    setCurrMapFunction,
    setItemIdFunction,
    setItemTypeFunction
  ) => {
    const locationData = await getLocationDetails(locationId);
    if (locationData) {
      if (firstItemId !== -1) {
        if (firstItemType === 'location' && Number(firstItemId) === Number(locationId)) {
          const msg = "Can't map a container to itself.";
          setShowAlert(true);
          setShowAlertMessage(msg);
          return;
        }
      }

      setItemIdFunction(locationId);
      setItemTypeFunction(itemType);

      const isMapped = locationData?.parentLocation !== null;
      const hierarchy = isMapped ? buildHierarchy(locationData.parentLocation, 'location') : null;
      setCurrMapFunction(isMapped ? [{ name: hierarchy, type: 'container' }] : []);
      setItemFunction(locationData);
    }
  };

  const clearItem = async (pos) => {
    // 1. Capture current state for the backend update
    const firstId = pos === 1 ? -1 : firstItemId;
    const firstType = pos === 1 ? '' : firstItemType;
    const secondId = pos === 2 ? -1 : secondItemId;
    const secondType = pos === 2 ? '' : secondItemType;

    // 2. Update local state immediately
    if (pos === 1) {
      setFirstItemId(-1);
      setFirstItemType('');
      setFirstItemDetails({});
      setFirstCurrentlyMappedTo([]);
    } else {
      setSecondItemId(-1);
      setSecondItemType('');
      setSecondItemDetails({});
      setSecondCurrentlyMappedTo([]);
    }
    
    // 3. Sync with backend asynchronously
    const newState = {
      firstItemId: firstId,
      firstItemType: firstType,
      secondItemId: secondId,
      secondItemType: secondType
    };
    
    // Non-blocking backend sync
    (async () => {
      try {
        if (newState.firstItemId === -1 && newState.secondItemId === -1) {
          await ActiveScanService.clearActiveScan();
        } else {
          await ActiveScanService.saveActiveScan(newState);
        }
      } catch (error) {
        console.error("Failed to sync clear action with backend:", error);
      }
    })();
  };

  const showItemDetails = (details, itemType, pos) => {
    if (!details || Object.keys(details).length === 0) {
      return <div className={styles.itemBox}>No details available</div>;
    }
    return (
      <div className={styles.itemBox}>
        <button 
          className={styles.itemDismissBtn} 
          onClick={(e) => { e.stopPropagation(); clearItem(pos); }}
          title="Clear this item"
        >
          ×
        </button>
        <h3 className={styles.itemTitle}>
          {itemType === "asset" ? "Asset Details" : "Container Details"}
        </h3>
        <p><strong>ID:</strong> {itemType === "asset" ? details?.assetId : details?.id}</p>
        <p><strong>Name:</strong> {details?.name}</p>
        <p><strong>Description:</strong> {details?.description}</p>
        {itemType === "asset" ? (
          <>
            <p><strong>Container:</strong> {details.location ? buildHierarchy(details.location, 'location') : '-'}</p>
            <p><strong>Parent Asset:</strong> {details.parentAsset ? buildHierarchy(details.parentAsset, 'asset') : '-'}</p>
          </>
        ) : (
          <p><strong>Parent Container:</strong> {details.parentLocation ? buildHierarchy(details.parentLocation, 'location') : '-'}</p>
        )}
      </div>
    );
  };

  const getLastValueFromUrl = (url) => {
    if (!url) return "";
    const cleanUrl = url.endsWith('/') ? url.slice(0, -1) : url;
    const segments = cleanUrl.split('/');
    return segments[segments.length - 1];
  };

  const scannedItemType = (result) => {
    if (result.includes("location")) return "location";
    if (result.includes("asset")) return "asset";
    return "unknown";
  };

  const setupScanResults = async (result) => {
    let itemType = scannedItemType(result);
    let itemId = getLastValueFromUrl(result);

    if (itemType === "unknown") {
      // Check if it's a URL that needs resolving (e.g. Kutt link)
      if (result.startsWith("http")) {
        try {
          const resolvedData = await QRTokenService.resolveUrl(result);
          if (resolvedData && resolvedData.resolvedUrl && resolvedData.resolvedUrl !== result) {
            console.log("Resolved URL:", resolvedData.resolvedUrl);
            return setupScanResults(resolvedData.resolvedUrl);
          }
        } catch (error) {
          console.error("Error resolving shortened URL:", error);
        }
      }

      try {
        const tokenStatus = await QRTokenService.verifyToken(itemId);
        if (tokenStatus) {
          if (tokenStatus.assigned) {
            if (tokenStatus.assetId) {
              itemType = "asset";
              itemId = tokenStatus.assetId;
            } else if (tokenStatus.locationId) {
              itemType = "location";
              itemId = tokenStatus.locationId;
            }
          } else {
            if (firstItemId !== -1 && firstItemDetails && firstItemDetails.name) {
              if (window.confirm(`Assign new QR code to ${firstItemType === 'asset' ? 'Asset' : 'Container'} "${firstItemDetails.name}"?`)) {
                  try {
                      if (firstItemType === 'asset') {
                          await updateAsset(firstItemId, { ...firstItemDetails, qrToken: itemId });
                      } else {
                          await updateLocation(firstItemId, { ...firstItemDetails, qrToken: itemId });
                      }
                      toast.success("QR code assigned successfully!");
                      await ActiveScanService.clearActiveScan();
                      await fetchRecentScans();
                      navigate(`/public/${firstItemType}s/${itemId}`);
                  } catch (e) {
                      console.error(e);
                      toast.error("Failed to assign QR code");
                  }
              }
              return;
            }

            toast.info("Unassigned QR token scanned. Redirecting to registration...");
            await QRTokenService.markAsScanned(itemId).catch(e => console.error(e));
            navigate(`/admin?page=scan_registration&qrToken=${itemId}`);
            return;
          }
        }
      } catch (error) {
        // Not a preallocated token
      }
    }

    if (itemType === "unknown") {
      toast.warning("Unknown item scanned.");
      return;
    }

    let setItemFunction, setCurrMapFunction, setItemIdFunction, setItemTypeFunction;

    if (firstItemId === -1) {
      setItemIdFunction = setFirstItemId;
      setItemTypeFunction = setFirstItemType;
      setItemFunction = setFirstItemDetails;
      setCurrMapFunction = setFirstCurrentlyMappedTo;
    } else {
      setItemIdFunction = setSecondItemId;
      setItemTypeFunction = setSecondItemType;
      setItemFunction = setSecondItemDetails;
      setCurrMapFunction = setSecondCurrentlyMappedTo;
    }

    if (itemType === "asset") {
      handleAssetDetails(itemId, itemType, setItemFunction, setCurrMapFunction, setItemIdFunction, setItemTypeFunction);
    } else if (itemType === "location") {
      handleLocationDetails(itemId, itemType, setItemFunction, setCurrMapFunction, setItemIdFunction, setItemTypeFunction);
    }
  };

  const handleScan = async (result) => {
    if (result) {
      setupScanResults(result);
      setQrScanning(false);
      stopCamera();
      if (barcodeInputRef.current) barcodeInputRef.current.value = "";
    }
  };

  const handleError = (error) => console.error("QR Code Error:", error);
  const setStreamParent = (stream) => { streamRef.current = stream; };

  const clearScans = async () => {
    setFirstItemId(-1);
    setSecondItemId(-1);
    setFirstItemType('');
    setSecondItemType('');
    setFirstItemDetails({});
    setSecondItemDetails({});
    setFirstCurrentlyMappedTo([]);
    setSecondCurrentlyMappedTo([]);
    if (barcodeInputRef.current) barcodeInputRef.current.value = "";
    
    // Clear on backend
    await ActiveScanService.clearActiveScan();
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  const getMovementDetails = () => {
    if (!firstItemType || !secondItemType) return null;

    let fromItem = null;
    let toItem = null;
    let description = "";
    let direction = "forward"; // forward: 1->2, reverse: 2->1

    if (firstItemType === "asset" && secondItemType === "location") {
      fromItem = firstItemDetails;
      toItem = secondItemDetails;
      description = `Placing asset "${firstItemDetails.name}" INSIDE container "${secondItemDetails.name}"`;
      direction = "forward";
    } else if (firstItemType === "location" && secondItemType === "location") {
      fromItem = firstItemDetails;
      toItem = secondItemDetails;
      description = `Placing container "${firstItemDetails.name}" INSIDE container "${secondItemDetails.name}"`;
      direction = "forward";
    } else if (firstItemType === "location" && secondItemType === "asset") {
      fromItem = firstItemDetails;
      toItem = secondItemDetails;
      description = `INVALID: Cannot place container "${firstItemDetails.name}" INSIDE asset "${secondItemDetails.name}"`;
      direction = "blocked";
    } else if (firstItemType === "asset" && secondItemType === "asset") {
      fromItem = firstItemDetails;
      toItem = secondItemDetails;
      description = `Nesting asset "${firstItemDetails.name}" INSIDE parent asset "${secondItemDetails.name}"`;
      direction = "forward";
    }

    return { fromItem, toItem, description, direction };
  };

  const swapItems = () => {
    if (firstItemId === -1 || secondItemId === -1) return;

    // Swap IDs
    const tempId = firstItemId;
    setFirstItemId(secondItemId);
    setSecondItemId(tempId);

    // Swap Types
    const tempType = firstItemType;
    setFirstItemType(secondItemType);
    setSecondItemType(tempType);

    // Swap Details
    const tempDetails = firstItemDetails;
    setFirstItemDetails(secondItemDetails);
    setSecondItemDetails(tempDetails);

    // Swap Mappings
    const tempMappings = firstCurrentlyMappedTo;
    setFirstCurrentlyMappedTo(secondCurrentlyMappedTo);
    setSecondCurrentlyMappedTo(tempMappings);
  };

  const movement = getMovementDetails();

  const moveItem = () => {
    if (firstItemType === "asset" && secondItemType === "location") {
      if (Number(firstItemDetails.location?.id) === Number(secondItemId)) {
        toast.info(`"${firstItemDetails.name}" is already inside "${secondItemDetails.name}".`);
        return;
      }
      if (window.confirm(`Move "${firstItemDetails.name}" into "${secondItemDetails.name}"?`)) {
        assignLocation(firstItemId, secondItemId);
      }
    } else if (firstItemType === "location" && secondItemType === "location") {
      if (Number(firstItemDetails.parentLocation?.id) === Number(secondItemId)) {
        toast.info(`"${firstItemDetails.name}" is already inside "${secondItemDetails.name}".`);
        return;
      }
      if (Number(secondItemDetails.parentLocation?.id) === Number(firstItemId)) {
        toast.error(`Conflict: "${secondItemDetails.name}" is already inside "${firstItemDetails.name}". Move aborted to prevent cycles.`);
        return;
      }
      if (window.confirm(`Move "${firstItemDetails.name}" inside "${secondItemDetails.name}"?`)) {
        assignChildLocation(firstItemId, secondItemId);
      }
    } else if (firstItemType === "location" && secondItemType === "asset") {
      toast.error("Invalid movement: Containers cannot be placed inside Assets.");
      return;
    } else if (firstItemType === "asset" && secondItemType === "asset") {
      if (Number(firstItemDetails.parentAsset?.id) === Number(secondItemId)) {
        toast.info(`"${firstItemDetails.name}" is already inside "${secondItemDetails.name}".`);
        return;
      }
      if (Number(secondItemDetails.parentAsset?.id) === Number(firstItemId)) {
        toast.error(`Conflict: "${secondItemDetails.name}" is already inside "${firstItemDetails.name}". Move aborted to prevent cycles.`);
        return;
      }
      if (window.confirm(`Move "${firstItemDetails.name}" inside "${secondItemDetails.name}"?`)) {
        assignParent(firstItemId, secondItemId);
      }
    } else {
      toast.error("Invalid operation.");
    }
  };

  return (
    <div className={styles.home}>
      <div className={styles.qrSection}>
        <h4 className={styles.heading}>Scan</h4>
        <input
          ref={barcodeInputRef}
          type="text"
          className={styles.barcodeInput}
          placeholder="Enter code manually"
          onKeyDown={(e) => e.key === "Enter" && handleScan(e.target.value)}
          autoFocus
        />
        {!qrScanning ? (
          <button className={styles.scanButton} onClick={() => setQrScanning(true)}>Start Scanner</button>
        ) : (
          <>
            <div className={styles.scannerContainer}>
              <QrScanner onScan={handleScan} onError={handleError} facingMode="environment" setStreamParent={setStreamParent} />
            </div>
            <button className={styles.scanButton} onClick={() => { stopCamera(); setQrScanning(false); }}>Stop</button>
          </>
        )}

        {recentScans.length > 0 && !qrScanning && (
          <div className={styles.recentScansArea}>
            <div className={styles.recentHeader}>
              <h5>Recently Scanned (Unassigned)</h5>
              <button 
                className={styles.refreshBtn} 
                onClick={(e) => { e.preventDefault(); fetchRecentScans(); }}
                title="Refresh Scans"
              >
                ↻
              </button>
            </div>
            <div className={styles.recentCardList}>
              {recentScans.slice(0, 3).map((scan) => (
                <div key={scan.token} className={styles.unassignedCard}>
                  <div className={styles.unassignedCardHeader}>
                    <div className={styles.unassignedBadge}>NEW TOKEN</div>
                    <button 
                      className={styles.unassignedDismissBtn} 
                      onClick={(e) => { e.stopPropagation(); handleDismissScan(e, scan.token); }}
                      title="Dismiss"
                    >
                      ×
                    </button>
                  </div>
                  <div className={styles.unassignedCardBody}>
                    <div className={styles.unassignedTokenInfo}>
                      <span className={styles.unassignedLabel}>QR TOKEN:</span>
                      <span className={styles.unassignedValue}>{scan.token}</span>
                    </div>
                    <div className={styles.unassignedTime}>
                      Scanned at {new Date(scan.lastScannedAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                    </div>
                  </div>
                  <button 
                    className={styles.registerButton}
                    onClick={() => navigate(`/admin?page=scan_registration&qrToken=${scan.token}`)}
                  >
                    Register This Item
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}


      </div>
      {(firstItemType || secondItemType) && (
        <div className={styles.scannedResultSection}>
          {movement && firstItemType && secondItemType && (
            <div className={styles.movementSummary}>
              <div className={styles.summaryIcon}><FaExclamationTriangle /></div>
              <div className={styles.summaryText}>{movement.description}</div>
            </div>
          )}
          <div className={styles.container}>
            {firstItemType && (
              <div className={`${styles.itemWrapper} ${movement?.direction === 'forward' ? styles.fromItem : movement?.direction === 'reverse' ? styles.toItem : ''}`}>
                <div className={styles.itemRoleBadge}>{movement?.direction === 'forward' ? 'INSIDE' : movement?.direction === 'reverse' ? 'OUTSIDE' : 'ITEM 1'}</div>
                {showItemDetails(firstItemDetails, firstItemType, 1)}
                {firstCurrentlyMappedTo.length > 0 && firstCurrentlyMappedTo.map((map, idx) => (
                  <div key={`first-map-${idx}`} className={styles.warningCard} style={{ marginTop: '15px' }}>
                    <div className={styles.warningContent}>
                      <FaExclamationTriangle className={styles.warningIcon} />
                      <p>
                        <strong>{firstItemDetails?.name}</strong> is already assigned to {map.type}: <strong>{map.name}</strong>.
                      </p>
                    </div>
                    <button
                      className={styles.unlinkButton}
                      onClick={() => {
                        if (window.confirm(`Remove ${map.type} link from ${firstItemDetails.name}?`)) {
                          if (firstItemType === 'asset') {
                            if (map.type === 'container') assignLocation(firstItemId, -1);
                            else assignParent(firstItemId, -1);
                          } else {
                            assignChildLocation(firstItemId, -1);
                          }
                        }
                      }}
                    >
                      Unlink
                    </button>
                  </div>
                ))}
              </div>
            )}

            {firstItemType && secondItemType && (
              <div className={`${styles.movementArrow} ${movement?.direction === 'reverse' ? styles.reversed : ''}`}>
                <div className={styles.arrowLine}></div>
                <div className={styles.arrowHead}><i className="fa-solid fa-chevron-right"></i></div>
              </div>
            )}

            {secondItemType && (
              <div className={`${styles.itemWrapper} ${movement?.direction === 'forward' ? styles.toItem : movement?.direction === 'reverse' ? styles.fromItem : ''}`}>
                <div className={styles.itemRoleBadge}>{movement?.direction === 'forward' ? 'OUTSIDE' : movement?.direction === 'reverse' ? 'INSIDE' : 'ITEM 2'}</div>
                {showItemDetails(secondItemDetails, secondItemType, 2)}
                {secondCurrentlyMappedTo.length > 0 && secondCurrentlyMappedTo.map((map, idx) => (
                  <div key={`second-map-${idx}`} className={styles.warningCard} style={{ marginTop: '15px' }}>
                    <div className={styles.warningContent}>
                      <FaExclamationTriangle className={styles.warningIcon} />
                      <p>
                        <strong>{secondItemDetails?.name}</strong> is already assigned to {map.type}: <strong>{map.name}</strong>.
                      </p>
                    </div>
                    <button
                      className={styles.unlinkButton}
                      onClick={() => {
                        if (window.confirm(`Remove ${map.type} link from ${secondItemDetails.name}?`)) {
                          if (secondItemType === 'asset') {
                            if (map.type === 'container') assignLocation(secondItemId, -1);
                            else assignParent(secondItemId, -1);
                          } else {
                            assignChildLocation(secondItemId, -1);
                          }
                        }
                      }}
                    >
                      Unlink
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          {firstItemType && secondItemType && (
            <div className={styles.actionButtons}>
              <button
                onClick={moveItem}
                className={styles.moveButton}
                disabled={movement?.direction === 'blocked'}
              >
                Confirm & Move
              </button>
              <button onClick={swapItems} className={styles.swapButton}>Swap Positions</button>
              <button onClick={clearScans} className={styles.clearButton}>Clear Scans</button>
            </div>
          )}
          {!secondItemType && (
             <div className={styles.activeScanInstructions}>
                Scan another item to perform a movement or assignment.
             </div>
          )}
        </div>
      )}
      {showAlert && <AlertModal message={showAlertMessage} onClose={handleCloseAlert} />}
    </div>
  );
};

export default Home;
